import React from "react";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { positionLabel } from "v2/constants/positions";

const getInitials = (name) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const AttendancePlayerList = ({
  players,
  selectedPlayerId,
  onPlayerSelect,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#111827]">
            {t("team.playerList.title")}
          </h3>
          <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-medium text-[#6B7280]">
            {players.length}
          </span>
        </div>
      </div>

      {/* Scrollable Player List */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {players.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB]">
              <Users size={22} className="text-[#6B7280]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("team.playerList.emptyTitle")}
            </h3>
            <p className="max-w-[200px] text-center text-xs leading-relaxed text-[#6B7280]">
              {t("team.attendancePlayerList.emptyBody")}
            </p>
          </div>
        ) : (
          players.map((player) => {
            const isSelected = player.id === selectedPlayerId;
            return (
              <div
                key={player.id}
                onClick={() => onPlayerSelect?.(player)}
                className={`flex cursor-pointer items-center gap-3 border-b border-[#F3F4F6] px-4 py-3 transition duration-150 last:border-b-0 ${
                  isSelected
                    ? "border-l-2 border-l-[#E07A3A] bg-[#FEF7F3]"
                    : "border-l-transparent border-l-2 hover:bg-[#FAFAFA]"
                }`}
              >
                {/* Avatar */}
                <div
                  className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                    isSelected ? "bg-[#E07A3A]" : "bg-[#F3F4F6]"
                  }`}
                >
                  <span
                    className={`text-xs font-semibold ${
                      isSelected ? "text-white" : "text-[#6B7280]"
                    }`}
                  >
                    {getInitials(player.name)}
                  </span>
                </div>

                {/* Player Info */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-[#111827]">
                    {player.name}
                  </p>
                  <p className="truncate text-[11px] text-[#9CA3AF]">
                    {positionLabel(player.position)}
                  </p>
                </div>

                {/* Attendance Rate + Progress Bar */}
                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                  <span className="text-xs font-semibold text-[#111827]">
                    {player.rate}%
                  </span>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-[#F3F4F6]">
                    <div
                      className="h-full rounded-full bg-[#E07A3A] transition-all duration-300"
                      style={{ width: `${player.rate}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AttendancePlayerList;
